import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { ChannelIcon } from 'v2/shared/components/channel-icons'
import { ConvChannels } from 'v2/types/api/conversations'
import z from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import { v2validations } from '../../config/validation.config'
import type { AgentPresetConversationNodeformData } from '../../types'

// Default values for all channel-specific fields
const CHANNEL_FIELD_DEFAULTS = {
  from: '',
  to: '',
  text: '',
  message_media: [],
  whatsapp_media: [],
  whatsapp_msg_type: 'text',
  template: {},
  variables: { header: [], body: [] },
  content_type: 'text',
  chat_media: {},
  header_media: {},
  title: '',
  button_description: '',
  buttons: [],
  header_type: 'none',
  header_text: '',
  body: '',
  footer: '',
  whatsapp_list: { button: '' },
  sections: [],
} as const

// Helper function to reset all channel-specific fields
const resetChannelFields = (form: GenericFormType<AgentPresetConversationNodeformData>) => {
  Object.entries(CHANNEL_FIELD_DEFAULTS).forEach(([key, value]) => {
    form?.setFieldValue(key as keyof AgentPresetConversationNodeformData, value)
  })
}

export const commonFields = (
  nodeVars: string[],
): GenericField<AgentPresetConversationNodeformData>[] => {
  const validationSchema = v2validations({ nodeVars }).agent_preset_conversation.innerType()

  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validationSchema.shape.name,
      componentType: 'input',
      inputProps: {
        type: 'text',
      },
    },
    {
      name: 'channel',
      label: 'Channel',
      zodValidation: validationSchema.shape.channel,
      componentType: 'select',
      inputProps: {
        options: [
          {
            label: 'Message',
            value: 'message',
            icon: <ChannelIcon channel={ConvChannels.MESSAGE} className="h-4 w-4" />,
          },
          {
            label: 'WhatsApp',
            value: 'whatsapp',
            icon: <ChannelIcon channel={ConvChannels.WHATSAPP} className="h-4 w-4" />,
          },
          {
            label: 'Chat',
            value: 'chat',
            icon: <ChannelIcon channel={ConvChannels.CHAT} className="h-4 w-4" />,
          },
        ],
      },
      afterOnChange: (_, __, form) => {
        // Reset all channel-specific fields when channel changes
        resetChannelFields(form as GenericFormType<AgentPresetConversationNodeformData>)
      },
    },
  ]
}

export const textFields = (
  nodeVars: string[],
): GenericField<AgentPresetConversationNodeformData>[] => {
  return [
    {
      name: 'text',
      label: 'Text',
      componentType: 'textarea',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp') {
          if (values.whatsapp_msg_type !== 'text') return z.string().optional()

          const hasAudioOrDoc = values.whatsapp_media?.some(
            (media) =>
              media.media_type?.includes('audio') || media.media_type?.includes('application'),
          )

          if (hasAudioOrDoc)
            return z.string().max(0, 'Text must be empty when audio or document is uploaded')

          return z.string().min(1, 'Text is required when WhatsApp message type is text')
        } else if (values.channel === 'message') {
          return z.string().min(1, 'Text is required')
        } else if (values.channel === 'chat') {
          return z.string().min(1, 'Text is required').max(1024, 'Max 1024 characters')
        }
        return z.string().optional()
      },
      visible: (values) => {
        if (values.channel === 'whatsapp') {
          return ['text', 'whatsapp_media'].includes(values.whatsapp_msg_type || '')
        } else if (values.channel === 'message') {
          return true
        } else if (values.channel === 'chat') {
          return values.content_type === 'text'
        }
        return false
      },
      onChangeListenTo: ['channel', 'whatsapp_msg_type', 'content_type'],
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=text]" />
        ),
      },
    },
  ]
}

export const waitForResponseFields = (
  nodeVars: string[],
): GenericField<AgentPresetConversationNodeformData>[] => {
  const validationSchema = v2validations({ nodeVars }).agent_preset_conversation.innerType()

  return [
    {
      name: 'wait_for_response',
      label: 'Wait for response',
      componentType: 'checkbox',
      zodValidation: validationSchema.shape.wait_for_response,
    },
    {
      name: 'reply_timeout',
      label: 'Reply timeout',
      componentType: 'inputNumber',
      visible: (values: AgentPresetConversationNodeformData) => values.wait_for_response,
      zodValidation: validationSchema.shape.reply_timeout,
      onChangeListenTo: ['wait_for_response'],
      inputProps: {
        min: 60,
        max: 3600,
      },
    },
  ]
}
